import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { Helpers } from '../../../utils/helpers';
import {
  IApiResponse,
  IMembershipTypeDto,
  IMembershipTypeAddRequest,
  ISystemCodeDto,
  IMembershipTypeAddResult
} from '../../../utils/constants/interfaces';
import { useApi, useToaster } from '../../../utils/hooks';
import { ToastStyle } from '../../../utils/hooks/useToaster';

// Default MembershipType
const defaultMembershipType = {
  id: 0,
  name: '',
  categoryCodeId: null,
  classCodeId: null,
  initiationFee: 0,
  dues: 0,
  description: '',
  sortOrder: 0,
  familyMembers: false,
  createdBy: '',
  lastModifiedBy: ''
} as unknown as IMembershipTypeDto;

const useMembershipTypeEditor = (membershipTypeId: string | null) => {
  const api = useApi();
  const navigate = useNavigate();
  const toaster = useToaster();
  const loadedOptionsRef = useRef(false);
  const loadingMembershipTypeRef = useRef(false);

  const [membershipType, setMembershipType] = useState<IMembershipTypeDto>(defaultMembershipType);
  const [originalMembershipType, setOriginalMembershipType] =
    useState<IMembershipTypeDto>(defaultMembershipType);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [loadingMembershipType, setLoadingMembershipType] = useState<boolean>(true);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState<boolean>(false);
  const [categoryOptions, setCategoryOptions] = useState<ISystemCodeDto[]>([]);
  const [classOptions, setClassOptions] = useState<ISystemCodeDto[]>([]);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(true);

  // Load dropdown options
  const loadDropdownOptions = useCallback(async () => {
    if (loadedOptionsRef.current) return; // Skip if already loaded
    setLoadingOptions(true);
    try {
      // Load category codes
      const categoryResponse = await api.get<ISystemCodeDto[]>(
        '/codes/code-types-by-type/TYPE_CATEGORY'
      );
      if (categoryResponse?.status?.statusCode === 200 && categoryResponse.response) {
        setCategoryOptions(categoryResponse.response);
      } else {
        setCategoryOptions([]);
      }
      // Load class codes
      const classResponse = await api.get<ISystemCodeDto[]>('/codes/code-types-by-type/TYPE_CLASS');
      if (classResponse?.status?.statusCode === 200 && classResponse.response) {
        setClassOptions(classResponse.response);
      } else {
        setClassOptions([]);
      }
    } catch (error) {
      setCategoryOptions([]);
      setClassOptions([]);
      const errorMessage = error instanceof Error ? error.message : String(error);
      toaster.addToast(`Error loading dropdown options: ${errorMessage}`, ToastStyle.ERROR, 5000);
    } finally {
      loadedOptionsRef.current = true;
      setLoadingOptions(false);
    }
  }, [api, toaster]);

  // Load membership type data
  const loadMembershipType = useCallback(
    async (membershipTypeId: string | null): Promise<void> => {
      if (loadingMembershipTypeRef.current) return;

      setLoadingMembershipType(true);
      loadingMembershipTypeRef.current = true;

      try {
        if (!Helpers.isNullOrWhiteSpace(membershipTypeId)) {
          if (membershipTypeId === 'new') {
            setMembershipType(defaultMembershipType);
            setOriginalMembershipType(defaultMembershipType);
            setIsDirty(false);
          } else {
            setFieldErrors([]);
            const endpoint = `/membership-types/${membershipTypeId}`;
            const apiResponse = await api.get<IMembershipTypeDto>(endpoint);
            if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
              const responseData = apiResponse.response;
              const updatedMembershipType: IMembershipTypeDto = {
                ...responseData,
                categoryCodeId: responseData.categoryCode ? responseData.categoryCode.id : 0,
                classCodeId: responseData.classCode ? responseData.classCode.id : 0
              };
              setMembershipType(updatedMembershipType);
              setOriginalMembershipType(updatedMembershipType);
            } else {
              await navigate('/membership-types?error=loading+membership+type');
            }
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        toaster.addToast(`Error loading membership type: ${errorMessage}`, ToastStyle.ERROR, 5000);
        await navigate('/membership-types?error=loading+membership+type');
      } finally {
        setLoadingMembershipType(false);
        loadingMembershipTypeRef.current = false;
      }
    },
    [api, navigate, toaster]
  );

  const updateValue = (key: string, value: string | number | boolean | null): void => {
    if (isSaving || !membershipType) return;
    setIsDirty(true);

    let errors = [...fieldErrors];
    errors = errors.filter((x) => !x.endsWith(`:${key}`));
    setFieldErrors(errors);

    const updatedMembershipType = { ...membershipType, [key]: value };
    setMembershipType(updatedMembershipType);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (Helpers.isNullOrWhiteSpace(membershipType.name)) {
      errors.push('overview:name');
    }
    if (membershipType.categoryCodeId === null || membershipType.categoryCodeId === undefined) {
      errors.push('overview:categoryCodeId');
    }
    if (membershipType.classCodeId === null || membershipType.classCodeId === undefined) {
      errors.push('overview:classCodeId');
    }
    if (membershipType.initiationFee < 0) {
      errors.push('overview:initiationFee');
    }
    if (membershipType.dues < 0) {
      errors.push('overview:dues');
    }
    setFieldErrors(errors);
    return errors.length === 0;
  };

  const performSave = async (): Promise<IApiResponse<
    IMembershipTypeDto | IMembershipTypeAddResult
  > | null> => {
    const isValid = validate();
    if (!isValid) {
      toaster.addToast(`Unable to save — required fields are missing.`, ToastStyle.ERROR, 5000);
      Helpers.scrollToClassName('is-invalid', -20);
      return null;
    }

    setIsSaving(true);
    try {
      const isNew = membershipType.id === 0;

      if (isNew) {
        const addRequest: IMembershipTypeAddRequest = {
          name: membershipType.name,
          categoryCodeId:
            membershipType.categoryCodeId === null ? 0 : membershipType.categoryCodeId,
          classCodeId: membershipType.classCodeId === null ? 0 : membershipType.classCodeId,
          initiationFee: membershipType.initiationFee,
          dues: membershipType.dues,
          description: membershipType.description,
          sortOrder: membershipType.sortOrder,
          familyMembers: membershipType.familyMembers
        };

        const endpoint = `/membership-types`;
        return await api.post<IMembershipTypeAddResult>(endpoint, JSON.stringify(addRequest));
      } else {
        const endpoint = `/membership-types/${membershipType.id}`;
        const membershipTypeToSave = { ...membershipType };

        if (membershipTypeToSave.categoryCodeId === null) {
          membershipTypeToSave.categoryCodeId = 0; // Use 0 if null
        }
        if (membershipTypeToSave.classCodeId === null) {
          membershipTypeToSave.classCodeId = 0; // Use 0 if null
        }
        return await api.put<IMembershipTypeDto>(endpoint, JSON.stringify(membershipTypeToSave));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const saveChanges = async (): Promise<void> => {
    const apiResponse = await performSave();
    if (apiResponse === null) {
      // TODO: Add better handling
      return; // Validation failed
    }

    if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
      setIsDirty(false);
      toaster.addToast(`Membership type saved successfully!`, ToastStyle.SUCCESS, 2000);

      let savedTypeId: string | null = null;
      const isNew = membershipType.id === 0;

      if (isNew) {
        const addResult = apiResponse.response as IMembershipTypeAddResult;

        if (addResult.succeeded && addResult.membershipTypeId) {
          savedTypeId = addResult.membershipTypeId;
          if (savedTypeId) {
            try {
              window.location.href = `/membership-types/${savedTypeId}`;
            } catch (error) {
              toaster.addToast(`Error navigating to new membership type.`, ToastStyle.ERROR, 5000);
            }
          }
        }
      } else {
        setMembershipType(apiResponse.response as IMembershipTypeDto);
        setOriginalMembershipType(apiResponse.response as IMembershipTypeDto);
      }
    } else {
      toaster.addToast(`Error saving membership type`, ToastStyle.ERROR, 8000);
    }
  };

  const saveAndBack = async (): Promise<void> => {
    const apiResponse = await performSave();
    if (apiResponse === null) {
      // TODO: Add better handling
      return; // Validation failed
    }

    if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
      toaster.addToast(`Membership type saved successfully!`, ToastStyle.SUCCESS, 2000);
      await navigate('/membership-types');
    } else {
      toaster.addToast(`Error saving membership type`, ToastStyle.ERROR, 8000);
    }
  };

  const saveAndNew = async (): Promise<void> => {
    const apiResponse = await performSave();
    if (apiResponse === null) {
      // TODO: Add better handling
      return; // Validation failed
    }

    if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
      toaster.addToast(`Membership type saved successfully!`, ToastStyle.SUCCESS, 2000);
      setMembershipType(defaultMembershipType);
      setIsDirty(false);
      setFieldErrors([]);

      if (membershipTypeId !== 'new') {
        await navigate('/membership-types/new');
      }
    } else {
      toaster.addToast(`Error saving membership type`, ToastStyle.ERROR, 8000);
    }
  };

  const cancelChanges = useCallback(() => {
    setMembershipType(originalMembershipType);
    setIsDirty(false);
    setFieldErrors([]);
  }, [originalMembershipType]);

  const back = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
      return;
    }
    setMembershipType(defaultMembershipType);
    void navigate('/membership-types');
  };

  const closeUnsavedChangesDialog = () => {
    setShowUnsavedChangesDialog(false);
  };

  const discardUnsavedChanges = () => {
    setShowUnsavedChangesDialog(false);
    setIsDirty(false);
    setMembershipType(defaultMembershipType);
    void loadMembershipType(membershipTypeId);
  };

  // Load data on mount or ID change
  useEffect(() => {
    void loadMembershipType(membershipTypeId);
  }, [membershipTypeId]);

  // Load dropdown options on mount
  useEffect(() => {
    void loadDropdownOptions();
  }, []);

  // Return hook API
  return {
    membershipType,
    isSaving,
    fieldErrors,
    loadingMembershipType,
    loadingOptions,
    showUnsavedChangesDialog,
    categoryOptions,
    classOptions,
    back,
    updateValue,
    saveChanges,
    saveAndBack,
    saveAndNew,
    discardUnsavedChanges,
    closeUnsavedChangesDialog,
    cancelChanges,
    ToastContainer: toaster.ToastContainer
  };
};

export default useMembershipTypeEditor;
